import {DocumentTypeVal, PersonLegalRole} from '@modifi/enums'
import {AnyAction, updateFactory} from '@modifi/redux'
import {aPush} from '@modifi/router'
import {
  filterDocumentsByInputFieldName,
  DIRECTOR_COUNTRY,
  DIRECTOR_NATIONALITY,
  DIRECTOR_POLITICALLY_EXPOSED,
  DIRECTOR_POLITICALLY_EXPOSED_REASON,
  DIRECTORS_DOCUMENT_INPUT_FIELD_NAMES,
  aUserFeedBackSubmitSuccessful,
  mapDirectorToFormData,
  mapDirectorToRequest,
  pickDirectorDocumentProperties,
  pickDirectorPersonProperties,
  pickDirectorRoleProperties,
  DIRECTORS_ROLE_INPUT_FIELD_NAMES,
} from '@modifi/plugin-onboarding'
import {
  ComplianceDirectorView,
  PatchPersonIdentificationRequestView,
  PatchPersonRequestView,
} from '@modifi/onboarding-api-clients'
import {getLogger} from '../../../../utils'
import {
  aFetchDocumentsByExternalPersonId,
  aUpdatePersonDocuments,
  selectDocumentsByExternalPersonId,
} from '../../../../data/person-documents'
import {aCreatePerson, aUpdatePerson} from '../../../../modules/persons'
import {aClearDataSourcePerson, selectDataSourcePerson} from './data-source'
import {aLinkPersonToLegalRole, aUpdatePersonToLegalRoleLink} from '../../../../modules/legal-roles'
import {ROUTE_COMPANY_PROFILE_DIRECTORS} from '../../../../routes/constants'
import {selectInputFieldNamesBySectionName} from '../../../../modules/input-fields'
import {SECTION_DIRECTORS} from '../../../../constants/sections'
import {AppThunkAction} from '../../../../types/redux'
import {
  aAppendDirectorToCompany,
  aFetchDirectors,
  aUpdateLocalDirector,
  selectDirectorByExternalId,
  selectDirectorsWhereLoaded,
} from '../../../../data/directors'
import {aFetchCompany, selectCurrentCompaniesCountryCode} from '../../../../data/company'
import {
  aFetchBeneficialOwners,
  selectBeneficialOwnerByExternalId,
} from '../../../../data/beneficial-owners'
import {aFetchInputFields} from '../../../../data/input-fields'
import {
  aFetchLegalRepresentatives,
  selectLegalRepresentativeByExternalId,
} from '../../../../data/legal-representatives'

const logger = getLogger('directors-page')

export const selectInitializePagePending = (
  state: Record<string, any>,
  externalPersonId: string
): boolean => {
  if (externalPersonId) {
    const DirectorsWhereLoaded = selectDirectorsWhereLoaded(state)
    if (!DirectorsWhereLoaded) {
      return true
    }
  }
  return false
}

export const selectInitialFormData = (
  state: Record<string, any>,
  externalPersonId: string
): {defaultValues: Record<string, any>; initialValues: Record<string, any>} => {
  const inputFieldsToRender = selectInputFieldNamesBySectionName(state, SECTION_DIRECTORS)

  const defaultValues: Record<string, any> = {
    [DIRECTOR_POLITICALLY_EXPOSED_REASON]: '',
    [DIRECTOR_POLITICALLY_EXPOSED]: false,
  }
  const initialValues: Record<string, any> = {
    'dataSource.externalPersonId': '',
    'dataSource.showSelect': false,
  }
  Object.assign(
    initialValues,
    DIRECTORS_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
      // eslint-disable-next-line no-param-reassign
      result[inputName] = []
      return result
    }, {} as Record<string, []>)
  )

  const dataSourceExternalPersonId = selectDataSourcePerson(state)

  if (externalPersonId) {
    const documents =
      selectDocumentsByExternalPersonId(
        state,
        externalPersonId
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      )?.map(({fileId, documentType}) => ({fileId, documentType: documentType!})) || []

    Object.assign(
      initialValues,
      DIRECTORS_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
        // eslint-disable-next-line no-param-reassign
        result[inputName] = filterDocumentsByInputFieldName(inputName, documents)
        return result
      }, {} as Record<string, any>)
    )

    // externalPersonId is a director
    const {address, ...rest} = selectDirectorByExternalId(state, externalPersonId) || {}
    Object.assign(
      initialValues,
      mapDirectorToFormData({
        ...address,
        ...rest,
      })
    )
  } else if (dataSourceExternalPersonId) {
    const documents =
      selectDocumentsByExternalPersonId(
        state,
        dataSourceExternalPersonId
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      )?.map(({fileId, documentType}) => ({fileId, documentType: documentType!})) || []

    Object.assign(
      initialValues,
      DIRECTORS_DOCUMENT_INPUT_FIELD_NAMES.reduce((result, inputName) => {
        // eslint-disable-next-line no-param-reassign
        result[inputName] = filterDocumentsByInputFieldName(inputName, documents)
        return result
      }, {} as Record<string, any>)
    )

    const {
      address,
      externalId: _,
      ...rest
    } = selectLegalRepresentativeByExternalId(state, dataSourceExternalPersonId) ||
    selectBeneficialOwnerByExternalId(state, dataSourceExternalPersonId) ||
    {}

    Object.assign(
      initialValues,
      mapDirectorToFormData({
        ...address,
        ...rest,
      })
    )
  } else {
    if (inputFieldsToRender.includes(DIRECTOR_NATIONALITY)) {
      defaultValues[DIRECTOR_NATIONALITY] = selectCurrentCompaniesCountryCode(state)
    }
    defaultValues[DIRECTOR_COUNTRY] = selectCurrentCompaniesCountryCode(state)
  }

  return {
    initialValues,
    defaultValues,
  }
}

export const aInitializePage =
  (externalCompanyId: string, externalPersonId?: string): AppThunkAction<Promise<void>> =>
  async (dispatch): Promise<void> => {
    // always there when company was loaded
    // dispatch(aEnsureDirectors())
    if (externalPersonId) {
      dispatch(aFetchDocumentsByExternalPersonId(externalPersonId))
    }
  }

type DocumentChange = {
  added: {fileId: string; documentType: DocumentTypeVal}[]
  removed: string[]
}

const {reducer, actions, selectors} = updateFactory({
  rootSelector: state => state?.pages?.directors?.form,
  actionNamePrefix: 'pages/directors/form',
  submitRequest:
    (a, changes: Record<string, any>, externalCompanyId: string, externalPersonId?: string) =>
    async (dispatch, getState): Promise<void> => {
      logger.info('Submitting Director page')
      logger.debug('changes: %o', changes)

      const documentChanges = Object.values(pickDirectorDocumentProperties(changes))
      const personChangesAsRequestData = mapDirectorToRequest(
        pickDirectorPersonProperties(changes)
      ) as PatchPersonRequestView
      const roleChangesAsRequestData = mapDirectorToRequest(
        pickDirectorRoleProperties(changes)
      ) as PatchPersonIdentificationRequestView

      let updatedPerson

      dispatch(a.pendingAction())

      const createdDirector: ComplianceDirectorView = {
        externalId: externalPersonId,
      }
      if (!externalPersonId && typeof changes['dataSource.externalPersonId'] === 'string') {
        // eslint-disable-next-line no-param-reassign
        externalPersonId = changes['dataSource.externalPersonId']
        Object.assign(
          createdDirector,
          selectLegalRepresentativeByExternalId(getState(), externalPersonId) ||
            selectBeneficialOwnerByExternalId(getState(), externalPersonId)
        )

        // adding legal role changes when creating director from existing person since BE
        // updates legal role fields for what it recieves and doesn't automatically add based upon
        // existing person id
        DIRECTORS_ROLE_INPUT_FIELD_NAMES.forEach((directorRoleInputField: string) => {
          const roleInputField = directorRoleInputField.replace('director.', '')
          // @ts-expect-error
          if (roleChangesAsRequestData[roleInputField] === undefined) {
            // @ts-expect-error
            roleChangesAsRequestData[roleInputField] = createdDirector[roleInputField]
          }
        })
      }

      const updateLegalRole =
        !!externalPersonId && !!selectDirectorByExternalId(getState(), externalPersonId)

      let existingPersonUpdated = false
      try {
        logger.info('Create or update the person: %o', personChangesAsRequestData)
        if (externalPersonId) {
          if (Object.keys(personChangesAsRequestData).length > 0) {
            existingPersonUpdated = true
            logger.debug('Person already exists - externalId is: %s, updating...', externalPersonId)
            updatedPerson = await dispatch(
              aUpdatePerson(externalPersonId, personChangesAsRequestData) as any
            )
            Object.assign(createdDirector, updatedPerson)
          } else {
            logger.debug('No person related changes detected')
          }
        } else {
          logger.debug('Person does not exist yet, creating...')
          updatedPerson = await dispatch(aCreatePerson(personChangesAsRequestData) as any)
          Object.assign(createdDirector, updatedPerson)
          // eslint-disable-next-line no-param-reassign
          externalPersonId = updatedPerson.externalId
        }

        logger.info('Creating or updating the person director link: %o', roleChangesAsRequestData)

        if (!externalPersonId) {
          throw new Error(`Unknown externalPersonId`)
        }

        if (updateLegalRole) {
          if (Object.keys(roleChangesAsRequestData).length > 0) {
            logger.debug('Person is already a director, updating...')

            Object.assign(createdDirector, roleChangesAsRequestData)
            // @todo handle address?
            dispatch(
              aUpdateLocalDirector(
                createdDirector as ComplianceDirectorView & {
                  externalId: NonNullable<ComplianceDirectorView['externalId']>
                },
                true
              )
            )

            await dispatch(
              aUpdatePersonToLegalRoleLink(
                externalPersonId,
                PersonLegalRole.DIRECTOR,
                roleChangesAsRequestData
              ) as unknown as AnyAction
            )
          } else {
            logger.debug('No role related changes detected')
          }
        } else {
          logger.debug('Person is not yet a director, creating...')

          Object.assign(createdDirector, roleChangesAsRequestData)
          await dispatch(
            aLinkPersonToLegalRole(
              externalPersonId,
              PersonLegalRole.DIRECTOR,
              roleChangesAsRequestData
            )
          )

          dispatch(
            aAppendDirectorToCompany(
              createdDirector as ComplianceDirectorView & {
                externalId: NonNullable<ComplianceDirectorView['externalId']>
              }
            )
          )
        }

        logger.info('Linking & unlinking documents to the person: %o', documentChanges)
        if (documentChanges.length > 0) {
          const mergedChanges = documentChanges.reduce(
            (result, change: DocumentChange) => {
              if (change.added && change.added.length > 0) {
                result.added.push(...change.added)
              }
              if (change.removed && change.removed.length > 0) {
                result.removed.push(...change.removed)
              }
              return result
            },
            {added: [], removed: []} as DocumentChange
          )
          await dispatch(aUpdatePersonDocuments(externalPersonId, mergedChanges))
        } else {
          logger.debug('No document related changed detected')
        }
      } catch (error: any) {
        logger.error('Error while creating or updating a person: %o', error)
        dispatch(a.errorAction(error))
        return
      }

      await Promise.all([
        dispatch(aFetchInputFields()),
        dispatch(aFetchCompany()),
        dispatch(aFetchDirectors()),
      ])

      // if Exisiting person updated is also a UBO or Legal Rep then fetch UBOs
      // or Legal Reps to update the their tables respectively in UI
      if (
        existingPersonUpdated &&
        !!selectLegalRepresentativeByExternalId(getState(), externalPersonId)
      ) {
        await dispatch(aFetchLegalRepresentatives())
      }
      if (
        existingPersonUpdated &&
        !!selectBeneficialOwnerByExternalId(getState(), externalPersonId)
      ) {
        await dispatch(aFetchBeneficialOwners())
      }

      // dispatch(aFetchPersonDocuments(externalPersonId))

      // redirect to directory summary page
      dispatch(aPush(ROUTE_COMPANY_PROFILE_DIRECTORS))

      dispatch(a.successAction())
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_DIRECTORS))
    },

  reset:
    a =>
    (dispatch): void => {
      dispatch(a.clearAction())
      dispatch(aClearDataSourcePerson())
    },
})

export default reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateHasError: selectSaveHasError,
  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors

export const {aReset, aSubmit: aSavePage} = actions
